import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import { LoginContext } from '../../context/LoginContext';
import {
  cancelConnectionRequest,
  fetchConnectionStatus,
  notifyRequestsChanged,
  sendConnectionRequest,
} from '../../services/connectionRequests';
import { requestsPathFor } from '../../utils/pendingRequest';

const MAX_MESSAGE_CHARS = 500;

/** Counts code points so the limit matches the server's mb_strlen (an emoji counts as 1). */
const charCount = (text) => Array.from(text || '').length;

const formatDate = (ymd) => {
  const date = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? ymd
    : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Request controls shown under the existing "Message Now" button on another member's profile.
 * Renders nothing for free members with no existing request (they keep the existing upgrade behaviour).
 */
const ConnectionRequestButton = ({ otherUserId, otherName }) => {
  const { userId, isPremium } = useContext(LoginContext);
  const navigate = useNavigate();
  const [relation, setRelation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');

  const loadStatus = useCallback(() => {
    if (!userId || !otherUserId || String(userId) === String(otherUserId)) return;
    fetchConnectionStatus(userId, otherUserId)
      .then((res) => {
        setRelation(res.response === 'success'
          ? { state: res.state, requestId: res.request_id, cooldownUntil: res.cooldown_until }
          : null);
      })
      .catch(() => setRelation(null));
  }, [userId, otherUserId]);

  useEffect(() => {
    setRelation(null);
    setFeedback('');
    loadStatus();
  }, [loadStatus]);

  const length = charCount(message.trim());
  const tooLong = length > MAX_MESSAGE_CHARS;

  const closeModal = () => {
    if (sending) return;
    setShowModal(false);
    setError('');
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (sending || tooLong) return;
    setSending(true);
    setError('');
    try {
      const res = await sendConnectionRequest(userId, otherUserId, message.trim());
      if (res.response === 'success') {
        setShowModal(false);
        setMessage('');
        setFeedback('Request sent. You will be able to chat once it is accepted.');
        notifyRequestsChanged();
        loadStatus();
      } else if (res.response === 'upgrade') {
        setShowModal(false);
        navigate(`/upgrade/${userId}`);
      } else if (res.code === 'duplicate' || res.code === 'reverse_pending' || res.code === 'already_connected') {
        setShowModal(false);
        setFeedback(res.message);
        loadStatus();
      } else {
        setError(res.message || 'Could not send the request. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async () => {
    if (cancelling || !relation?.requestId) return;
    if (!window.confirm('Withdraw your request?')) return;
    setCancelling(true);
    try {
      const res = await cancelConnectionRequest(userId, relation.requestId);
      setFeedback(res.response === 'success' ? 'Request withdrawn.' : (res.message || 'Could not cancel the request.'));
      notifyRequestsChanged();
      loadStatus();
    } catch (err) {
      setFeedback('Network error. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  if (!relation) return null;

  let content = null;
  if (relation.state === 'accepted') {
    content = <p className="connection-request__status mb-0">✓ You are connected with {otherName || 'this member'}.</p>;
  } else if (relation.state === 'pending_sent') {
    content = (
      <div className="connection-request__status d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>Request sent · awaiting response</span>
        <button type="button" className="btn btn-sm btn-outline-danger" disabled={cancelling} onClick={handleCancel}>
          {cancelling ? 'Cancelling…' : 'Cancel request'}
        </button>
      </div>
    );
  } else if (relation.state === 'pending_received') {
    content = (
      <div className="connection-request__status d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>{otherName || 'This member'} sent you a request.</span>
        <Link to={requestsPathFor(relation.requestId)} className="btn btn-sm btn-danger">Respond</Link>
      </div>
    );
  } else if (isPremium) {
    content = relation.cooldownUntil ? (
      <p className="connection-request__status mb-0">
        You can send a new request after {formatDate(relation.cooldownUntil)}.
      </p>
    ) : (
      <button
        type="button"
        className="btn rounded-0 btn-lg d-block w-100 py-3 profile-cta-btn connection-request__send"
        onClick={() => { setFeedback(''); setShowModal(true); }}
      >
        <b>Send</b> Request
      </button>
    );
  }

  if (!content && !feedback) return null;

  return (
    <div className="connection-request">
      {content}
      {feedback && <p className="connection-request__feedback small mb-0 mt-2" role="status">{feedback}</p>}

      <Modal show={showModal} onHide={closeModal} centered>
        <form onSubmit={handleSend} noValidate>
          <Modal.Header closeButton>
            <Modal.Title as="h5">Send a connection request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <label htmlFor="connection-request-message" className="form-label">
              Message to {otherName || 'this member'} <span className="text-muted">(optional)</span>
            </label>
            <textarea
              id="connection-request-message"
              className={`form-control ${tooLong ? 'is-invalid' : ''}`}
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Introduce yourself in a few words…"
              aria-describedby="connection-request-count"
              disabled={sending}
            />
            <div className="d-flex justify-content-between mt-1">
              <small className="text-danger">{tooLong ? `Please keep it under ${MAX_MESSAGE_CHARS} characters.` : error}</small>
              <small id="connection-request-count" className={tooLong ? 'text-danger' : 'text-muted'}>
                {length}/{MAX_MESSAGE_CHARS}
              </small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-outline-secondary" onClick={closeModal} disabled={sending}>
              Cancel
            </button>
            <button type="submit" className="btn btn-danger" disabled={sending || tooLong} style={{ background: '#E53D5C', borderColor: '#E53D5C' }}>
              {sending ? 'Sending…' : 'Send request'}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <style>{`
        .connection-request { margin-top: 10px; }
        .connection-request__send {
          background: #fff;
          color: #E53D5C;
          border: 2px solid #E53D5C;
        }
        .connection-request__send:hover { background: #fdf2f4; color: #E53D5C; }
        .connection-request__status {
          background: #fdf2f4;
          border: 1px solid #f5c2cc;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 14px;
          color: #8a1f33;
        }
        .connection-request__feedback { color: #555; }
      `}</style>
    </div>
  );
};

export default ConnectionRequestButton;
